import React, { useMemo, useRef, useEffect } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { DeliveryPricingBands } from 'shop/types/cart'
import Band from './Band'
import {
  addDummyBand,
  getActiveBands,
  getDiscountCopy,
  calculateBandData,
  calculateProgress
} from './utils'

interface Props {
  deliveryPricingBands: DeliveryPricingBands[]
  subtotal: string | undefined
  cartLoading: boolean
}

const DiscountBasketValue: React.FC<Props> = ({
  deliveryPricingBands,
  subtotal,
  cartLoading
}) => {
  const prevActiveCount = useRef(0)

  // If delivery pricing bands don't start at 0, add dummy band for UI's sake
  const processedDeliveryPricingBands = useMemo(
    () => addDummyBand(deliveryPricingBands),
    [deliveryPricingBands]
  )

  /** Variables for use in UI: copy and current number of delivery bands that are active */
  const { copy, activeBandsCount, hasBandInProgress } = useMemo(() => {
    const inProgress = processedDeliveryPricingBands.some(
      ({ lowerThreshold, upperThreshold }) => {
        const isActive = subtotal
          ? parseFloat(subtotal) >= parseFloat(lowerThreshold)
          : false
        const progress =
          subtotal && isActive
            ? calculateProgress(subtotal, lowerThreshold, upperThreshold)
            : 0
        return progress > 0 && progress < 100
      }
    )

    return {
      copy: subtotal
        ? getDiscountCopy(subtotal, processedDeliveryPricingBands)
        : null,
      activeBandsCount: getActiveBands(subtotal, processedDeliveryPricingBands)
        .length,
      hasBandInProgress: inProgress
    }
  }, [subtotal, processedDeliveryPricingBands])

  useEffect(() => {
    if (!cartLoading) {
      prevActiveCount.current = activeBandsCount
    }
  }, [activeBandsCount, cartLoading])

  if (!subtotal) return null

  return (
    <div data-testid='discount-basket-value'>
      <Copy>{copy}</Copy>
      <Container>
        {processedDeliveryPricingBands
          // Filter last band from the UI only if there's more than one band
          .filter(
            (_, index) =>
              processedDeliveryPricingBands.length === 1 ||
              index < processedDeliveryPricingBands.length - 1
          )
          .map((band, index) => {
            const { id } = band
            const bandData = calculateBandData(
              band,
              subtotal,
              index,
              cartLoading,
              prevActiveCount.current,
              activeBandsCount,
              hasBandInProgress,
              processedDeliveryPricingBands.length
            )
            return (
              <Band key={id} id={id} {...bandData} cartLoading={cartLoading} />
            )
          })}
      </Container>
    </div>
  )
}

const Copy = styled.p(
  (): CSSObject => ({
    textAlign: 'left',
    color: '#59595A',
    fontSize: '13px',
    fontWeight: 500,
    margin: 0
  })
)

const Container = styled.div(
  (): CSSObject => ({
    display: 'flex',
    gap: '8px',
    padding: '8px 0'
  })
)

export default DiscountBasketValue
